import * as React from 'react';
import { I18n } from "react-redux-i18n";
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';;

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Questionnaires } from "@common/components/questionnaires";
import { QuestionnaireItemMapDto } from '@common/dto/questionnaire/questionnaire-item-map-dto';
import { HttpErrorDto } from '@common/dto/http';

import "./questionnaire-open.styles.scss";

export type QuestionnaireOpenProps = {
  itemName: string;
  onClose: () => void;
  showErrorMessage: (msg: string) => void;
  getQuestionnaires: () => Promise<Array<QuestionnaireItemMapDto>>;
  onSubmit: (selectedQuestionnaire: QuestionnaireItemMapDto) => Promise<void>; 
};

export const QuestionnaireOpen: React.FC<QuestionnaireOpenProps> = (props) => {
  const { onClose, onSubmit, itemName, showErrorMessage, getQuestionnaires } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState<QuestionnaireItemMapDto | undefined>();

  const handleSubmit = React.useCallback(async () => {
    if (!selectedQuestionnaire) {
      return;
    }

    setSubmitting(true);

    try {
      await onSubmit(selectedQuestionnaire);
    } catch (err) {
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t("Failed to open questionnaire"));
      setSubmitting(false);
      console.error(err);
    }
  }, [onSubmit, showErrorMessage, selectedQuestionnaire]);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="QuestionnaireOpen"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes({ id: 'lbl-bw-parent-header' })}
        >
          {I18n.t("Open Questionnaire for")} {itemName}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        <Questionnaires 
          getFhq4Value={(x: QuestionnaireItemMapDto) => x?.fhq4}
          getIdValue={(x: QuestionnaireItemMapDto) => x?.inviteId}
          columns={[
            { name: "Questionnaire Name", fieldName: "questionnaireName", width: '25%' },
            { name: "Status", fieldName: "status", width: '27%' },
            { name: "Date Started", fieldName: "dateStarted", width: '16%' },
            { name: "Date Completed", fieldName: "dateCompleted", width: '16%' },
            { name: "Invited By", fieldName: "invitedBy", width: '16%' },
          ].map((x) => ({ ...x, name: I18n.t(x.name) }))}
          getData={getQuestionnaires} 
          selectedQuestionnaire={selectedQuestionnaire} 
          onSelectedQuestionnaireChange={setSelectedQuestionnaire}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes({ id: 'btn-bw-parent-cancel' })}
          onClick={onClose}
        >
          {I18n.t("Cancel")}
        </Button>
        <Button
          disabled={submitting || selectedQuestionnaire === undefined}
          bsStyle="primary"
          {...getTestAttributes({ id: 'btn-bw-parent-next' })}
          onClick={handleSubmit}
        >
          {submitting ? I18n.t("Loading") : I18n.t('Launch Questionnaire')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
